'use client'

import {
  type AnimationEvent,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

interface BorderBeamProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode
  /** Turning it off fades the beam out (0.5s) rather than cutting it; on fades it back in. */
  active?: boolean
  /** Glow intensity, 0–1. Changes glide (0.4s) instead of jumping. */
  strength?: number
  /** Seconds per lap around the border. */
  duration?: number
  /** Corner radius in px; read from the wrapped element when omitted. */
  borderRadius?: number
}

/**
 * Rides an animated glow around the border of the element it wraps — our port of the
 * `border-beam` package's "md" preset (Libraries.dev Beam), in its default rainbow palette. The
 * layers live in app/globals.css ([data-beam]), so it needs no per-instance CSS and no
 * dependency. Pauses while scrolled offscreen and holds still under reduced motion.
 */
export function BorderBeam({
  children,
  active = true,
  strength = 1,
  duration,
  borderRadius,
  style,
  onAnimationEnd,
  ...props
}: BorderBeamProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isActive, setIsActive] = useState(active)
  const [isFading, setIsFading] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [detectedRadius, setDetectedRadius] = useState<number | null>(null)

  useEffect(() => {
    if (active && !isActive && !isFading) setIsActive(true)
    else if (!active && isActive && !isFading) setIsFading(true)
  }, [active, isActive, isFading])

  // Match the wrapped element's corners, so the beam rides its actual border.
  useEffect(() => {
    if (borderRadius != null) return
    const el = ref.current
    if (!el) return
    const detect = () => {
      const child = el.firstElementChild as HTMLElement | null
      if (!child) return
      const radius = parseFloat(getComputedStyle(child).borderTopLeftRadius)
      if (!Number.isNaN(radius) && radius > 0) setDetectedRadius(radius)
    }
    detect()
    const observer = new MutationObserver(detect)
    observer.observe(el, { childList: true })
    return () => observer.disconnect()
  }, [borderRadius, children])

  // Offscreen, the beam would still repaint every frame; pause it until it nears the viewport.
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setIsVisible(entry.isIntersecting)
      },
      { rootMargin: '256px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const handleAnimationEnd = useCallback(
    (event: AnimationEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && event.animationName === 'beam-fade-out') {
        setIsActive(false)
        setIsFading(false)
      }
      onAnimationEnd?.(event)
    },
    [onAnimationEnd]
  )

  const radius = borderRadius ?? detectedRadius
  return (
    <div
      {...props}
      ref={ref}
      data-beam=""
      data-active={isActive && !isFading ? '' : undefined}
      data-fading={isFading ? '' : undefined}
      data-paused={isActive && !isFading && !isVisible ? '' : undefined}
      onAnimationEnd={handleAnimationEnd}
      style={
        {
          ...style,
          '--beam-strength': Math.max(0, Math.min(1, strength)),
          ...(radius != null ? { '--beam-radius': `${radius}px` } : {}),
          ...(duration != null ? { '--beam-duration': `${duration}s` } : {}),
        } as CSSProperties
      }
    >
      {children}
      <div data-beam-bloom="" />
    </div>
  )
}
